/**
 * AboutPage
 * Über Anna Sacher + Kontaktformular
 */

import { useState, FormEvent } from 'react'
import { Seo } from '@/components/Seo'
import './AboutPage.css'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

export function AboutPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      const response = await fetch(
        'https://codepilot-api.arkturian.com/api/v1/contact/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            partner_id: 'annasacher',
            ...formData,
          }),
        }
      )

      if (!response.ok) {
        throw new Error('Fehler beim Senden der Nachricht')
      }

      setSuccess(true)
      setFormData({ name: '', email: '', subject: '', message: '' })
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="about-page">
      <Seo title="Über Anna Sacher Santana" description="Über Anna Sacher Santana und Kontakt." />
      <div className="about-container">
        {/* Bio Section */}
        <section className="about-section bio">
          <h1>Über Anna Sacher Santana</h1>

          <div className="bio-content">
            <div className="bio-text">
              <p>
                Anna Sacher Santana ist eine österreichische Bildhauerin, deren
                Werke die Verbindung zwischen Kunst, Heilung und menschlicher
                Psyche erforschen.
              </p>

              <p>
                Ihre Skulpturen entstehen aus der Überzeugung, dass Kunst nicht
                nur ästhetisch sein, sondern auch therapeutisch wirken kann.
                Durch die Nutzung von Spiegelneuronen ermöglichen ihre Werke
                den Betrachtern, innere Prozesse zu aktivieren und Harmonie zu
                erfahren.
              </p>

              <h3>Schwerpunkte</h3>
              <ul>
                <li>Psychoedukativ-therapeutische Skulpturen</li>
                <li>Energiefluss und Heilung</li>
                <li>Kinder- und Jugendpsychiatrie</li>
                <li>Symbolik und Mythologie</li>
              </ul>

              <h3>Material</h3>
              <p>
                Anna arbeitet primär mit rostfreiem Stahl, dessen glänzende
                Oberfläche Lichtspiegelungen ermöglicht und die Bewegung der
                Energie visualisiert. Metall als eines der fünf Elemente der
                chinesischen Heilkunst bringt energetisches Gleichgewicht in
                den Raum.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="about-section contact">
          <h2>Kontakt</h2>

          {success ? (
            <div className="success-message">
              <h3>Vielen Dank!</h3>
              <p>Ihre Nachricht wurde erfolgreich gesendet.</p>
              <button onClick={() => setSuccess(false)}>
                Weitere Nachricht senden
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-Mail</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Betreff</label>
                <input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Nachricht</label>
                <textarea
                  id="message"
                  rows={6}
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  required
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" disabled={submitting}>
                {submitting ? 'Wird gesendet...' : 'Nachricht senden'}
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  )
}
